/**
 * @ignore
 * Created by daletan on 7/6/16.
 */

'use strict';

const SV = require('server-vars');
const Price = require('bunsen/models/inventory/Price.es');
const PriceLock = require('bunsen/models/inventory/PriceLock.es');
const enabler = require('./enabler.es');
const get = require('lodash.get');

const getPriceIn = ({ itemModel, currency }) => {
    return function({ type }) {
        return itemModel.getPriceIn(type, currency);
    };
};

const getPriceByType = ({ itemModel, type }) => {
    return itemModel.getPriceByType(type) || {};
};

const createPriceTypes = ({ itemModel, currency }) => {
    const priceTypes = [Price.TYPE_NET, Price.TYPE_RETAIL, Price.TYPE_SALE];
    return priceTypes.reduce((prev, type) => {
        const model = getPriceByType({ itemModel, type });
        const price = getPriceIn({ itemModel, currency })({ type });
        if (model && price) {
            prev[type] = {
                model,
                price,
            };
        }
        return prev;
    }, {});
};

const getOriginalPrice = ({ itemModel, currency }) => {
    const model = itemModel.getPriceLocksByType(PriceLock.LOCK_TYPE_ORIGINAL);
    const price = itemModel.getPriceLocksIn(PriceLock.LOCK_TYPE_ORIGINAL, currency);
    return {
        model,
        price,
    };
};

const userIsLoggedOut = (userProfile = {}) => {
    return userProfile !== null && userProfile.type === 'NOT_LOGGED_IN';
};

const holdBelongsToUser = ({ userId, itemModel }) => {
    if (!itemModel || typeof itemModel.get !== 'function') {
        return false;
    }
    // Retrieve custom holds (injected via graphql)
    const customHold = itemModel.get('activeHold');
    if (customHold) {
        // If there is an active custom hold, get the buyer id
        const buyerId = get(customHold, 'buyer.serviceId', '');
        // If there is a custom hold, does is belong to the user?
        return buyerId === userId;
    }

    // Fallback to normal attrs if activeHold not injected
    const holds = itemModel.get('holds');
    const activeHold = holds ? holds.getActiveHold() : undefined;
    if (activeHold) {
        return userId === activeHold.get('buyer.id');
    }
    return false;
};

const priceHelpers = {
    /**
     * Get the main retail, net, and sale price data
     * Returns an object of different price types with
     * a `model` and `price` property
     * @param {object} userProfile
     * @param {object} itemModel
     * @param {string} currency
     * @returns {{salePriceData: { model: Backbone.Model, price}, netPriceData: (*|{}), retailPriceData: (*|{})}}
     */
    processPriceData({ userProfile = {}, itemModel, currency }) {
        const priceModels = createPriceTypes({ itemModel, currency });
        const originalPriceData = getOriginalPrice({ itemModel, currency });
        const salePriceData = priceModels[Price.TYPE_SALE] || {};
        const netPriceData = priceModels[Price.TYPE_NET] || {};
        const retailPriceData = priceModels[Price.TYPE_RETAIL] || {};
        const originalCurrency = itemModel.getCurrency();
        let basePrice;
        let discountedPrice;
        let percentageOff;

        if (salePriceData.price) {
            basePrice = itemModel.getPriceLocksIn(PriceLock.LOCK_TYPE_ORIGINAL, originalCurrency);
            discountedPrice = itemModel.getPriceIn(Price.TYPE_SALE, originalCurrency);
        } else if (netPriceData.price) {
            basePrice = itemModel.getPriceIn(Price.TYPE_RETAIL, originalCurrency);
            discountedPrice = itemModel.getPriceIn(Price.TYPE_NET, originalCurrency);
        }

        if (basePrice && discountedPrice) {
            percentageOff = priceHelpers.calculatePercentageOff({ discountedPrice, basePrice });
        }

        const priceData = {
            originalPriceData,
            salePriceData,
            retailPriceData,
            percentageOff,
        };

        if (!userIsLoggedOut(userProfile)) {
            priceData.netPriceData = netPriceData;
        }

        return priceData;
    },

    // If user is trade and item is in furniture vertical with a valid net price
    showNetPrice({ canUserRequestNetPrice, userProfile, itemModel, validNetPrice }) {
        if (userIsLoggedOut(userProfile)) {
            return false;
        }
        return !!(
            enabler.isEnabled('enable_net_price') &&
            !!validNetPrice &&
            itemModel.isFurniture() &&
            (!!SV.get('data.netPrice') || canUserRequestNetPrice)
        );
    },

    showSalePrice(salePrice, originalPrice) {
        return salePrice > 0 && salePrice < originalPrice;
    },

    calculatePercentageOff({ discountedPrice, basePrice }) {
        const percentage = discountedPrice / basePrice * 100;
        return `${Math.floor(100 - percentage)}%`;
    },

    getButtonOptions(props) {
        const { itemModel, itemProps, userProfile } = props;
        const { id: userId } = userProfile;
        const { canRequestNetPrice, salePriceData, originalPriceData } = itemProps;
        const salePrice = salePriceData.price;
        const originalPrice = originalPriceData.price;
        const showSalePrice = priceHelpers.showSalePrice(salePrice, originalPrice);
        const isNetPriceHidden = itemModel.isPriceHiddenByType(Price.TYPE_NET);
        const isRetailPriceHidden = itemModel.isPriceHiddenByType(Price.TYPE_RETAIL);
        const isRetailPriceNegotiable = itemModel.isNegotiablePriceByType(Price.TYPE_RETAIL);
        const isSalePriceNegotiable = itemModel.isNegotiablePriceByType(Price.TYPE_SALE);
        // Does the item have an unavailable reason code that is either 'ON_HOLD' of 'ON_HOLD_CUSTOM'?
        const isOnHold = itemModel.isOnHold();
        const isSold = itemModel.isSold();
        let showRequestPrice;
        let showPurchaseButton;
        let showMakeOffer;
        let showMakeOfferMC;

        if ((isOnHold && !holdBelongsToUser({ userId, itemModel })) || isSold) {
            // If the item is "On Hold" && the hold does not belong to the current user, then return no buttons
            showRequestPrice = false;
            showPurchaseButton = false;
            showMakeOffer = false;
            showMakeOfferMC = false;
        } else if (canRequestNetPrice && itemModel.isFurniture()) {
            // Otherwise, if Trade User viewing items that are in furniture vertical
            // Show the "Request Price" Button if item is not on sale and has a hidden net price (PUR)
            // Never show the "Request Price Button"
            showRequestPrice = !showSalePrice && isNetPriceHidden && isRetailPriceHidden;
            // Show the "Purchase" Button if item is on sale, has a valid net price, or a non-hidden list price
            showPurchaseButton = showSalePrice || !isNetPriceHidden || !isRetailPriceHidden;
            // Show the "Make Offer" Button if item is on sale and negotiable
            showMakeOffer = showSalePrice
                ? isSalePriceNegotiable
                : !isRetailPriceHidden && isRetailPriceNegotiable;
            // Show the "Make Offer Button" in Message Center if Sale Price is Negotiable or Retail Price is Negotiable, regardless of user type
            showMakeOfferMC =
                (showSalePrice && isSalePriceNegotiable) ||
                (!isRetailPriceHidden && isRetailPriceNegotiable);
        } else {
            // Show the "Request Price" Button if item is not on sale and has a hidden list/retail price (PUR)
            showRequestPrice = !showSalePrice && isRetailPriceHidden;
            // Show the "Purchase" Button if item is on sale, or has a non-hidden retail price
            showPurchaseButton = showSalePrice || !isRetailPriceHidden;
            // Show the "Make Offer" Button if item is on sale and negotiable, or is not on sale has a non-hidden, negotiable retail price
            showMakeOffer = showSalePrice
                ? isSalePriceNegotiable
                : !isRetailPriceHidden && isRetailPriceNegotiable;
            // Show the "Purchase Button" in Message Center if Sale Price is Negotiable or Retail Price is Negotiable, regardless of user type
            showMakeOfferMC =
                (showSalePrice && isSalePriceNegotiable) ||
                (!isRetailPriceHidden && isRetailPriceNegotiable);
        }

        return {
            showRequestPrice,
            showPurchaseButton,
            showMakeOffer,
            showMakeOfferMC,
        };
    },

    getPriceDisplayOptions(props) {
        const { itemModel, itemProps, userProfile } = props;
        const { id: userId } = userProfile;
        const { canRequestNetPrice, salePriceData, originalPriceData } = itemProps;
        const salePrice = salePriceData.price;
        const originalPrice = originalPriceData.price;
        const isNetPriceHidden = itemModel.isPriceHiddenByType(Price.TYPE_NET);

        const isRetailPriceHidden = itemModel.isPriceHiddenByType(Price.TYPE_RETAIL);
        const showSold = itemModel.isSold();
        // Does the item have an unavailable reason code that is either 'ON_HOLD' of 'ON_HOLD_CUSTOM'?
        const isOnHold = itemModel.isOnHold();
        // Show the "On Hold" custom display if there is a hold that belongs to user
        const showOnHoldCustom = holdBelongsToUser({ userId, itemModel });
        const showOnHold = isOnHold && !showOnHoldCustom;

        let showSalePrice = priceHelpers.showSalePrice(salePrice, originalPrice) && !isOnHold;
        let showNetPrice;
        let showRetailPrice = !isRetailPriceHidden;
        let showPriceUponRequest;
        let showListPriceLabel;

        if (showSold) {
            showSalePrice = false;
            showNetPrice = false;
            showRetailPrice = false;
            showPriceUponRequest = false;
            showListPriceLabel = false;
        } else {
            if (canRequestNetPrice && itemModel.isFurniture()) {
                // Otherwise, if Trade User viewing items that are in furniture vertical
                // Display "Net Price" if it is valid and if we do not display the "Sale Price"
                showNetPrice = !showSalePrice && !isNetPriceHidden;
                // Display "List Price Label" if we display the "List Price", but hide it if item is on sale
                showListPriceLabel = showRetailPrice && !showSalePrice;
            } else {
                // Otherwise, if non-Furniture-Trade User...
                // Never display "Net Price"
                showNetPrice = false;
                // Never Display "List Price Label"
                showListPriceLabel = false;
            }
            // Display "Price Upon Request" if we display neither "Net Price" nor "List Price"
            showPriceUponRequest = !showNetPrice && !showRetailPrice && !showSalePrice;
        }

        return {
            showSalePrice,
            showNetPrice,
            showRetailPrice,
            showPriceUponRequest,
            showListPriceLabel,
            showOnHoldCustom,
            showOnHold,
            showSold,
        };
    },
};

module.exports = priceHelpers;



// WEBPACK FOOTER //
// ./assets/js/global/utils/priceHelpers.es.js